"use client";

import { useState, useRef, useEffect } from "react";
import { X, ImagePlus, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useTeamBioStore, TeamBio } from "@/store/teamBioStore";
import { ActivityLogger } from "@/lib/activityLog";
import { prepareImageUpload } from "@/lib/image-upload";
import { getStrapiImageUrl } from "@/lib/image-utils";

interface CreateTeamBioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
  editTeamBio?: TeamBio | null;
}

const TEAM_OPTIONS = [
  { value: 'staff' as const, label: 'Staff' },
  { value: 'board' as const, label: 'Board' },
];

export default function CreateTeamBioModal({ isOpen, onClose, onResult, editTeamBio }: CreateTeamBioModalProps) {
  const { currentUser } = useAuth();
  const { createTeamBio, updateTeamBio } = useTeamBioStore();

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [team, setTeam] = useState<'staff' | 'board'>('staff');
  const [bio, setBio] = useState('');
  const [photo, setPhoto] = useState<File | null | undefined>(undefined);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setName('');
    setTitle('');
    setTeam('staff');
    setBio('');
    setPhoto(undefined);
    setPhotoPreview(null);
  };

  useEffect(() => {
    if (editTeamBio) {
      setName(editTeamBio.name);
      setTitle(editTeamBio.title);
      setTeam(editTeamBio.team);
      setBio(editTeamBio.bio);
      // undefined = photo unchanged; only set to null (clear) when user explicitly removes it
      setPhoto(undefined);
      const existingUrl = editTeamBio.photo?.url ? getStrapiImageUrl(editTeamBio.photo.url) : null;
      setPhotoPreview(existingUrl);
    } else {
      resetForm();
    }
  }, [editTeamBio, isOpen]);

  if (!isOpen) return null;

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { file: prepared, error } = await prepareImageUpload(file);
    if (error) { onResult?.(false, error); return; }
    setPhoto(prepared);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(prepared);
  };

  const removePhoto = () => {
    // null = explicitly cleared; store will send photo: null to Strapi to remove the relation
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!name.trim() || !title.trim() || !bio.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const data = { name: name.trim(), title: title.trim(), team, bio: bio.trim(), photo };
      const result = editTeamBio
        ? await updateTeamBio(editTeamBio.documentId, data)
        : await createTeamBio(data);

      if (result) {
        onResult?.(true, editTeamBio ? 'Team bio updated successfully' : 'Team bio created successfully');
        if (currentUser) {
          if (editTeamBio) ActivityLogger.editTeamBio(currentUser, data.name, editTeamBio.documentId);
          else ActivityLogger.createTeamBio(currentUser, data.name, result.documentId);
        }
        resetForm();
        onClose();
      } else {
        onResult?.(false, 'Failed to save team bio');
      }
    } catch (error) {
      console.error('src/components/ui/CreateTeamBioModal.tsx', error);
      onResult?.(false, 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = name.trim().length > 0 && title.trim().length > 0 && bio.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="create-team-bio-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold text-foreground">
            {editTeamBio ? 'Edit Team Bio' : 'Create Team Bio'}
          </h2>
          <button data-testid="create-team-bio-close-btn" onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Photo upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Photo</label>
            {photoPreview ? (
              <div>
                <div className="relative aspect-video rounded-md overflow-hidden border border-border">
                  <img src={photoPreview} alt="Photo preview" className="w-full h-full object-contain" />
                  <button
                    data-testid="create-team-bio-remove-photo-btn"
                    onClick={removePhoto}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div
                data-testid="create-team-bio-photo-dropzone"
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition"
              >
                <ImagePlus className="mx-auto mb-2 text-foreground-muted" size={32} />
                <p className="text-sm text-foreground-muted">Click to upload a photo</p>
                <p className="text-xs text-caption mt-1">PNG, JPG, WEBP</p>
              </div>
            )}
            <input
              data-testid="create-team-bio-photo-file-input"
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="create-team-bio-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter team member name"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="create-team-bio-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter team member title"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Team */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Team <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                data-testid="create-team-bio-team-dropdown-btn"
                type="button"
                onClick={() => setShowTeamDropdown((s) => !s)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-left text-sm text-foreground flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span>{TEAM_OPTIONS.find((o) => o.value === team)?.label ?? 'Select team'}</span>
                <ChevronDown size={16} className={`text-foreground-muted transition-transform ${showTeamDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTeamDropdown && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
                  {TEAM_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={opt.value === 'staff' ? 'create-team-bio-team-staff-option' : 'create-team-bio-team-board-option'}
                      type="button"
                      onClick={() => { setTeam(opt.value); setShowTeamDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-sidebar-hover transition ${team === opt.value ? 'text-primary font-medium' : 'text-foreground'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Team member bio <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="create-team-bio-bio-textarea"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Enter team member bio..."
              rows={5}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            data-testid="create-team-bio-cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="create-team-bio-submit-btn"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : (editTeamBio ? 'Update Team Bio' : 'Create Team Bio')}
          </button>
        </div>
      </div>
    </div>
  );
}
